import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { useAuth } from '../contexts/AuthContext';
import './TokenExpiryAlert.css';

const WARNING_MS = 30 * 60 * 1000;

function readExpiry(userId) {
  if (!userId) return null;

  try {
    const storage = window.localStorage;
    const seller = storage.getItem('mercadolibre_verified_user_id');
    const currentSeller = storage.getItem('mercadolibre_user_id');
    const token = storage.getItem('mercadolibre_access_token');

    if (!seller || seller !== currentSeller || !token) return null;

    let expiry = Number(storage.getItem('mercadolibre_token_expires_at'));

    if (!Number.isFinite(expiry) || expiry < 1000000000000) {
      const connection = JSON.parse(storage.getItem('ml_connection') || '{}');

      if (
        connection?.isConnected !== true ||
        String(connection?.userId || '') !== seller
      ) return null;

      expiry = Date.parse(connection?.tokenExpiry || '');
    }

    if (!Number.isFinite(expiry)) return null;

    return {
      expiry,
      key: `${userId}:${seller}:${expiry}`
    };
  } catch {
    return null;
  }
}

function formatTime(ms) {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function otherDialogOpen() {
  return [...document.querySelectorAll(
    '[role="dialog"], [role="alertdialog"], [aria-modal="true"]'
  )].some(element => {
    if (element.closest('.swal2-container')) return false;
    return element.getClientRects().length > 0;
  });
}

function goToConfiguration() {
  if (window.location.pathname !== '/api-configuration') {
    window.history.pushState({}, '', '/api-configuration');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

export default function TokenExpiryAlert() {
  const { user } = useAuth();
  const [now, setNow] = useState(Date.now());
  const [info, setInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const activeModal = useRef(null);

  // MB_TOKEN_FLOAT_PERSIST_DRAG_V1
  const [savedFloat, setSavedFloat] = useState(null);
  const [floatPosition, setFloatPosition] = useState(null);
  const dragCleanup = useRef(null);

  // Recordar exclusivamente la posición del recuadro.
  useEffect(() => {
    let position = null;

    if (user?.id) {
      try {
        const stored = JSON.parse(
          localStorage.getItem(`mb-token-position:${user.id}`) || 'null'
        );

        if (
          Number.isFinite(stored?.left) &&
          Number.isFinite(stored?.top)
        ) {
          position = {
            left: Math.max(
              0, Math.min(stored.left, window.innerWidth - 100)
            ),
            top: Math.max(
              0, Math.min(stored.top, window.innerHeight - 60)
            )
          };
        }
      } catch {}
    }

    setFloatPosition(position);
  }, [user?.id]);

  useEffect(() => () => {
    dragCleanup.current?.();
  }, []);

  const startFloatDrag = event => {
    // No interferir con el botón original de configuración.
    if (event.button !== 0 || event.target.closest('button')) return;

    dragCleanup.current?.();

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    let position = {
      left: rect.left,
      top: rect.top
    };

    const move = pointer => {
      position = {
        left: Math.max(
          0,
          Math.min(
            window.innerWidth - rect.width,
            pointer.clientX - offsetX
          )
        ),
        top: Math.max(
          0,
          Math.min(
            window.innerHeight - rect.height,
            pointer.clientY - offsetY
          )
        )
      };

      setFloatPosition(position);
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      dragCleanup.current = null;
    };

    const finish = () => {
      cleanup();

      if (user?.id) {
        try {
          localStorage.setItem(
            `mb-token-position:${user.id}`,
            JSON.stringify(position)
          );
        } catch {}
      }
    };

    dragCleanup.current = cleanup;

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);

    event.preventDefault();
  };

  useEffect(() => {
    const update = () => {
      setNow(Date.now());
      const next = readExpiry(user?.id);

      // Mantener intacta la información original de la conexión.
      setInfo(previous =>
        previous?.key === next?.key ? previous : next
      );

      // Recordar únicamente el aviso flotante, nunca el token.
      let remembered = null;

      if (user?.id) {
        try {
          const storageKey = `mb-token-float:${user.id}`;
          const saved = JSON.parse(
            localStorage.getItem(storageKey) || 'null'
          );

          const seller = localStorage.getItem(
            'mercadolibre_user_id'
          );

          if (next) {
            if (next.expiry - Date.now() <= WARNING_MS) {
              remembered = next;

              if (saved?.key !== next.key) {
                localStorage.setItem(
                  storageKey,
                  JSON.stringify({
                    expiry: next.expiry,
                    key: next.key,
                    seller
                  })
                );
              }
            } else if (saved) {
              // Conexión renovada: retirar aviso anterior.
              localStorage.removeItem(storageKey);
            }
          } else if (
            saved &&
            Number.isFinite(saved.expiry) &&
            saved.expiry > 1000000000000 &&
            typeof saved.key === 'string' &&
            (!seller || saved.seller === seller)
          ) {
            // Recuperar el recuadro si la conexión aún
            // no se ha reconstruido después de recargar.
            remembered = {
              expiry: saved.expiry,
              key: saved.key
            };
          }
        } catch {}
      }

      setSavedFloat(previous =>
        previous?.key === remembered?.key
          ? previous
          : remembered
      );
    };

    update();
    const timer = window.setInterval(update, 1000);
    window.addEventListener('storage', update);
    window.addEventListener('focus', update);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('storage', update);
      window.removeEventListener('focus', update);
    };
  }, [user?.id]);

  useEffect(() => {
    if (
      activeModal.current &&
      activeModal.current !== info?.key &&
      Swal.isVisible() &&
      document.querySelector('.mb-token-popup')
    ) {
      Swal.close();
    }
  }, [info?.key]);

  useEffect(() => {
    if (!info || info.expiry - now > WARNING_MS) return;
    if (activeModal.current || Swal.isVisible() || otherDialogOpen()) return;

    const warningKey = `mb-token-warning:${info.key}`;

    try {
      if (sessionStorage.getItem(warningKey) === 'shown') return;
      sessionStorage.setItem(warningKey, 'shown');
    } catch {
      if (activeModal.current === info.key) return;
    }

    activeModal.current = info.key;
    setModalOpen(true);

    const expired = info.expiry <= Date.now();
    let clockTimer;
    let changedToExpired = expired;

    Swal.fire({
      title: expired ? 'Conexión expirada' : 'Tu conexión está por vencer',
      icon: expired ? 'error' : 'warning',
      html: `
        <p>Tiempo restante de conexión</p>
        <div class="mb-token-modal-clock" id="mb-token-modal-clock">
          ${formatTime(info.expiry - Date.now())}
        </div>
        <p>Podrás continuar trabajando, pero necesitarás renovar la conexión.</p>
      `,
      confirmButtonText: expired ? 'Ir a configuración' : 'Entendido',
      showCloseButton: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
      buttonsStyling: false,
      customClass: {
        popup: 'mb-token-popup',
        confirmButton: 'mb-token-confirm'
      },
      showClass: { popup: 'mb-token-enter' },
      hideClass: { popup: 'mb-token-minimize' },
      didOpen: () => {
        clockTimer = window.setInterval(() => {
          const left = info.expiry - Date.now();
          const clock = document.getElementById('mb-token-modal-clock');
          if (clock) clock.textContent = formatTime(left);

          if (left <= 0 && !changedToExpired) {
            changedToExpired = true;
            Swal.update({
              title: 'Conexión expirada',
              icon: 'error',
              confirmButtonText: 'Ir a configuración'
            });
          }
        }, 1000);
      },
      willClose: () => window.clearInterval(clockTimer)
    }).then(result => {
      activeModal.current = null;
      setModalOpen(false);

      if (result.isConfirmed && Date.now() >= info.expiry) {
        goToConfiguration();
      }
    });

  }, [info, now]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (
        activeModal.current &&
        document.querySelector('.mb-token-popup') &&
        otherDialogOpen()
      ) {
        Swal.close();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // El aviso puede recuperarse sin alterar SweetAlert2.
  const visibleFloat = info || savedFloat;

  if (
    !visibleFloat ||
    visibleFloat.expiry - now > WARNING_MS ||
    modalOpen
  ) return null;

  const left = visibleFloat.expiry - now;
  const expired = left <= 0;
  const critical = left <= 5 * 60 * 1000;
  const urgent = left <= 10 * 60 * 1000;

  return (
    <div
      className={`mb-token-float ${critical ? 'mb-token-critical' : urgent ? 'mb-token-urgent' : ''}`}
      role="status"
      onPointerDown={startFloatDrag}
      style={floatPosition ? {
        left: `${floatPosition.left}px`,
        top: `${floatPosition.top}px`,
        right: 'auto',
        bottom: 'auto'
      } : undefined}
    >
      <div className="mb-token-float-header">
        <span className="mb-token-indicator">◷</span>
        <div>
          <div className="mb-token-float-title">
            {expired ? 'Conexión expirada' : 'Conexión próxima a vencer'}
          </div>
          {!expired && (
            <div className="mb-token-time">{formatTime(left)}</div>
          )}
        </div>
      </div>

      {expired && (
        <button
          type="button"
          className="mb-token-go"
          onClick={goToConfiguration}
        >
          Ir a configuración
        </button>
      )}
    </div>
  );
}
